interface IEventEmitter {
  addListener(event: string | symbol, listener: (...args: any[]) => void, once: boolean): this;

  on(event: string | symbol, listener: (...args: any[]) => void): this;

  once(event: string | symbol, listener: (...args: any[]) => void): this;

  removeListener(event: string | symbol, listener: (...args: any[]) => void): this;

  off(event: string | symbol, listener: (...args: any[]) => void): this;

  dispatchEvent(event: string | symbol, payload?: any): this;
}

interface IQueuePreloader {
  store: string[];
  maxConnections: number;

  complete: boolean;

  queue: string[];
  activeQueue: string[];

  setMaxConnections(connections: number): void;

  populateQueue(): void;

  load(any: any): any;
}

type ImagePreloaderOptions = {
  baseUrl: string;
  maxConnections: number;
}
type EventItem = {
  fn: Function;
  once: boolean;
}

export default class ImagePreloader implements IEventEmitter, IQueuePreloader {

  maxConnections;
  readonly store;
  readonly baseUrl;
  queue;
  activeQueue;
  private events: EventItem;

  public complete = false;
  public progress = 0;

  constructor(images = [], options?: ImagePreloaderOptions) {
    this.baseUrl = options.baseUrl || '';
    this.store = images ? images.slice(0) : [];
    this.maxConnections = options.maxConnections || 5;

    this.populateQueue();
  }

  addListener(event: string | symbol, listener: (...args: any[]) => void, once = false): this {
    const item = { fn: listener, once };
    this.events[event] = this.events[event] && this.events[event].length ? [...this.events[event], item] : [item];
    return this;
  }

  removeListener(event: string | symbol, listener?: (...args: any[]) => void): this {
    if (this.events[event] && listener) {
      this.events[event] = this.events[event].filter(({ fn }) => fn !== listener);
    } else {
      this.events[event] = [];
    }
    return this;
  }

  dispatchEvent(event: string | symbol, payload?: any): this {
    if (this.events[event]) {
      this.events[event]
        .forEach(({ fn, once }) => {
          if (once) {
            this.removeListener(event, fn);
          }
          fn(payload);
        });
    }
    return this;
  }

  once(event: string | symbol, listener: (...args: any[]) => void): this {
    return this.addListener(event, listener, true);
  }

  on(event: string | symbol, listener: (...args: any[]) => void): this {
    return this.addListener(event, listener, false);
  }

  off(event: string | symbol, listener?: (...args: any[]) => void): this {
    return this.removeListener(event, listener);
  }

  setMaxConnections(connections) {
    this.maxConnections = connections || 5;
  }

  populateQueue() {
    if (!this.queue.length && !this.activeQueue.length) {
      this.complete = true;
      this.dispatchEvent('complete');
    }
    for (let i = this.activeQueue.length; i < this.maxConnections; i++) {
      this.activeQueue.push(this.load(this.queue.shift()));
    }
  }

  load(image) {
    let img = new Image();
    img.src = this.baseUrl + image;
    img.onload = () => {
      this.progress = 1 - ((this.queue.length + this.activeQueue.length) / this.store.length);

      this.dispatchEvent('fileload', { id: this.baseUrl + image, filename: image })
        .dispatchEvent('progress', this.progress);

      this.activeQueue.splice(this.activeQueue.indexOf(image), 1);
      this.populateQueue();
    };
    img.onerror = (e) => {
      this.progress = 1 - ((this.queue.length + this.activeQueue.length) / this.store.length);

      this.dispatchEvent('error', e)
        .dispatchEvent('progress', this.progress);

      this.activeQueue.splice(this.activeQueue.indexOf(image), 1);
      this.populateQueue();
    };
    return img;
  }

  public static promiseLoader(url: string, width:string = "125", height:string = "125") {
    return new Promise<HTMLImageElement>(resolve => {
      const image = new Image();
      image.crossOrigin = 'anonymous';

      let formatPattern = /\.([0-9a-z]+)(?:[\?#]|$)/i;
      if(url.match(formatPattern)[1] === "svg"){
        let headers = new Headers();
        headers.append('pragma', 'no-cache');
        headers.append('cache-control', 'no-cache');
        headers.append('Access-Control-Allow-Origin','*');
        headers.append('Sec-Fetch-Mode','no-cors');
        fetch(url, {headers})
            .then(response => response.text())
            .then(svg => {
              const svgElement = new DOMParser().parseFromString(svg, "image/svg+xml").querySelector('svg');

              let svgWidth = svgElement.attributes.getNamedItem("width");
              let svgHeight = svgElement.attributes.getNamedItem("width");

              if(!svgWidth && !svgHeight){
                svgElement.setAttribute("width", width);
                svgElement.setAttribute("height", height);

                image.src = "data:image/svg+xml;base64,"+btoa(svgElement.outerHTML);
              }else image.src = url;

            })
            .catch(e => {
              console.log(e)
            })
      }else{
        image.src = url;
      }

      image.addEventListener('load', () => {
        resolve(image);
      });
    });
  }
}
